import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Stack,
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useRecoilValue } from 'recoil';
import { toast } from 'react-toastify';
import { selectedRegionState } from '../../../atoms/Forecast';
import { getRegions } from '../../../atoms/regions';
import { saveForecast } from '../../../repositories/HttpRepository';
import { ToastMessage } from '../../../constants/toastMessage';
import { WEEK_DAYS, WEEK_DAY_NAMES, getTicketTypeLabel, getChangedCellBgColor } from '../../../constants/forecast';
import { getBSType } from '../utils/getBSType';
import { getForecastIdByWeekDay } from '../utils/transformData';

const isValidPercentage = (value) => /^\d+$/.test(String(value)) && Number(value) >= 1 && Number(value) <= 200;

const toString = (percentage) => (percentage === null || percentage === undefined ? '' : String(percentage));

const AutoForecastSettingsModal = ({ open, onClose, forecasts = [], forecastByIdMap = new Map(), initialTicketType = null, onSaved }) => {
  const selectedRegion = useRecoilValue(selectedRegionState);
  const regions = useRecoilValue(getRegions);
  const regionLabel = regions.find((r) => r.value === selectedRegion)?.label || selectedRegion;

  const [ticketTypeTab, setTicketTypeTab] = useState(null);
  const [editedPercentageMap, setEditedPercentageMap] = useState(new Map()); // forecastId -> input string, only cells that differ from original
  const [saving, setSaving] = useState(false);

  const ticketTypes = useMemo(() => getBSType(forecasts), [forecasts]);
  const originalPercentageMap = useMemo(() => new Map([...forecastByIdMap].map(([id, forecast]) => [id, toString(forecast.percentage)])), [forecastByIdMap]);
  const ticketTypeByForecastId = useMemo(() => new Map(forecasts.map((f) => [f.id, f.ticket_type])), [forecasts]);
  const forecastIdByWeekDay = useMemo(() => getForecastIdByWeekDay(forecasts, ticketTypeTab), [forecasts, ticketTypeTab]);
  const dayRows = WEEK_DAYS.filter((day) => forecastIdByWeekDay.has(day));

  const changedTicketTypes = new Set([...editedPercentageMap.keys()].map((id) => ticketTypeByForecastId.get(id)));
  const allChangesValid = [...editedPercentageMap.values()].every(isValidPercentage);
  const canSave = !saving && editedPercentageMap.size > 0 && allChangesValid;
  
  useEffect(() => {
    if (open) {
      // Set default open tab, start with no edits
      setTicketTypeTab(initialTicketType ?? ticketTypes[0] ?? null);
      setEditedPercentageMap(new Map());
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const isChanged = (forecastId) => editedPercentageMap.has(forecastId);

  const onChangePercentage = (forecastId, value) => {
    setEditedPercentageMap((prev) => {
      const next = new Map(prev);
      if (value === originalPercentageMap.get(forecastId)) {
        next.delete(forecastId); // back to original → no longer a change
      } else {
        next.set(forecastId, value);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!canSave) {
      return;
    }

    // Only send forecasts whose percentage actually changed
    const changedItems = [...editedPercentageMap].map(([id, percentage]) => ({ id, percentage: Number(percentage) }));

    setSaving(true);
    try {
      const response = await saveForecast(changedItems);
      if (response.ok) {
        if (onSaved) {
          onSaved(changedItems);
        }
        toast.success(ToastMessage.UPDATED_SUCCESS);
        onClose();
      } else {
        toast.error(response?.data?.error || response?.data?.message || ToastMessage.UPDATED_FAIL);
      }
    } catch (error) {
      toast.error(error?.message || ToastMessage.UPDATED_FAIL);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        transition: {
          onExited: () => {
            setTicketTypeTab(null);
            setEditedPercentageMap(new Map());
          },
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            Auto Forecast Settings
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Region:{' '}
              <Typography component="span" variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>
                {regionLabel}
              </Typography>
            </Typography>
          </Box>
          <IconButton aria-label="close" onClick={onClose} disabled={saving}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack sx={{ gap: 2 }}>
          <Box sx={(theme) => ({ px: 2, py: 1.5, bgcolor: theme.colorTokens.surfaceTint8, borderRadius: 1 })}>
            <Typography variant="body2">
              Forecast = last week&apos;s actual routes × the % for that day. Each service tab has its own %. Only days with AUTO on are recalculated; manual edits are kept.
            </Typography>
          </Box>

          <Box>
            {ticketTypes.length > 0 && (
              <Tabs value={ticketTypeTab} onChange={(event, value) => setTicketTypeTab(value)} sx={{ minHeight: 40 }}>
                {ticketTypes.map((ticketType) => (
                  <Tab
                    key={ticketType}
                    value={ticketType}
                    sx={{ paddingTop: 0, paddingBottom: 0, minHeight: 40 }}
                    label={
                      <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
                        {getTicketTypeLabel(ticketType)}
                        {/* Dot is shown on unsaved changes tab */}
                        {changedTicketTypes.has(ticketType) &&<Box aria-label="unsaved changes" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />}
                      </Stack>
                    }
                  />
                ))}
              </Tabs>
            )}

            <Table size="small" sx={{ border: 1, borderColor: 'divider' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 700 }}>DAY</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '30%' }}>FORECAST %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dayRows.map((day) => {
                  const forecastId = forecastIdByWeekDay.get(day);
                  const value = editedPercentageMap.get(forecastId) ?? originalPercentageMap.get(forecastId) ?? '';
                  const invalid = isChanged(forecastId) && !isValidPercentage(value);
                  return (
                    <TableRow key={day}>
                      <TableCell>
                        <Typography component="label" htmlFor={`auto-forecast-${day}`} variant="body1" sx={{ fontWeight: 700 }}>
                          {WEEK_DAY_NAMES[day]}
                        </Typography>
                      </TableCell>
                      <TableCell sx={(theme) => ({ bgcolor: isChanged(forecastId) ? getChangedCellBgColor(theme) : 'transparent' })}>
                        <TextField
                          id={`auto-forecast-${day}`}
                          type="number"
                          size="small"
                          value={value}
                          error={invalid}
                          helperText={invalid ? 'Enter a whole number from 1 to 200.' : ''}
                          onChange={(e) => onChangePercentage(forecastId, e.target.value)}
                          disabled={saving}
                          sx={{
                            width: 140,
                          }}
                          slotProps={{
                            input: {
                              endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            },
                            htmlInput: { min: 1, max: 200, step: 1 },
                            formHelperText: { sx: { whiteSpace: 'nowrap', mx: 0 } },
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Only days this region operates are shown. Changes are logged in the table history.
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={!canSave}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoForecastSettingsModal;
